var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IssueStatusEnum } from '../types';
import { bulkUndoStatusChange, undoStatusChange } from '../services/IIMIssuesService';
import { iimReviewKeys } from './queryKeys';
/**
 * Hook for updating a single IIM issue with optimistic updates
 * @returns Mutation object for updating issue status and manager notes
 */
export function useUndoStatusIssue({ issueId, inspectionId }) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => undoStatusChange(issueId),
        // Since we don't know the previous status, we'll just update the timestamp optimistically
        onMutate: () => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches for ALL issues queries (list + grid)
            yield queryClient.cancelQueries({ queryKey: iimReviewKeys.issuesAll(inspectionId) });
            yield queryClient.cancelQueries({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) });
            // Snapshot previous values for ALL issues queries (list + grid)
            const previousQueries = [];
            queryClient.getQueriesData({ queryKey: iimReviewKeys.issuesAll(inspectionId) }).forEach(([queryKey, data]) => {
                previousQueries.push({ queryKey, data });
            });
            queryClient
                .getQueriesData({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) })
                .forEach(([queryKey, data]) => {
                previousQueries.push({ queryKey, data });
            });
            // Optimistically revert the issue to its pre-action state so the UI
            // responds immediately without waiting for the slow server response.
            // The server response in onSuccess will correct any discrepancy.
            const applyOptimisticUndo = (oldData) => {
                if (!(oldData === null || oldData === void 0 ? void 0 : oldData.issues))
                    return oldData;
                return Object.assign(Object.assign({}, oldData), { issues: oldData.issues.map((issue) => {
                        var _a, _b;
                        if (issue.id !== issueId)
                            return issue;
                        // Mirror what the backend does: clear markedForSuggestion first,
                        // then if status was changed (dismissed/suggested), revert to detected.
                        const prevStatus = issue.markedForSuggestion || issue.status === IssueStatusEnum.Detected
                            ? issue.status
                            : IssueStatusEnum.Detected;
                        return Object.assign(Object.assign({}, issue), { markedForSuggestion: false, status: prevStatus, statusChanges: (_b = (_a = issue.statusChanges) === null || _a === void 0 ? void 0 : _a.slice(0, -1)) !== null && _b !== void 0 ? _b : [], updatedAt: new Date().toISOString() });
                    }) });
            };
            // Optimistically update ALL cache entries (list + grid)
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesAll(inspectionId) }, applyOptimisticUndo);
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) }, applyOptimisticUndo);
            // Return snapshots for rollback
            return { previousQueries };
        }),
        // If the mutation fails, rollback ALL cache entries
        onError: (err, variables, context) => {
            if (context === null || context === void 0 ? void 0 : context.previousQueries) {
                context.previousQueries.forEach(({ queryKey, data }) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            console.error('Error undoing status change:', err);
        },
        // On success, update ALL cache entries with the actual response from the server (list + grid)
        onSuccess: (data) => {
            const applySuccessUpdate = (oldData) => {
                if (!(oldData === null || oldData === void 0 ? void 0 : oldData.issues))
                    return oldData;
                return Object.assign(Object.assign({}, oldData), { issues: oldData.issues.map((issue) => (issue.id === issueId ? data : issue)) });
            };
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesAll(inspectionId) }, applySuccessUpdate);
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) }, applySuccessUpdate);
        }
    });
}
/**
 * Hook for bulk undoing status changes for multiple IIM issues
 * @returns Mutation object for bulk undoing status changes
 */
export function useBulkUndoIssues({ inspectionId, issueIds }) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => bulkUndoStatusChange(issueIds),
        // Optimistic update
        onMutate: () => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches for ALL issues queries (list + grid)
            yield queryClient.cancelQueries({ queryKey: iimReviewKeys.issuesAll(inspectionId) });
            yield queryClient.cancelQueries({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) });
            // Snapshot previous values for ALL issues queries (list + grid)
            const previousQueries = [];
            queryClient.getQueriesData({ queryKey: iimReviewKeys.issuesAll(inspectionId) }).forEach(([queryKey, data]) => {
                previousQueries.push({ queryKey, data });
            });
            queryClient
                .getQueriesData({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) })
                .forEach(([queryKey, data]) => {
                previousQueries.push({ queryKey, data });
            });
            const applyUpdate = (oldData) => {
                if (!(oldData === null || oldData === void 0 ? void 0 : oldData.issues))
                    return oldData;
                return Object.assign(Object.assign({}, oldData), { issues: oldData.issues.map((issue) => issueIds.includes(issue.id)
                        ? Object.assign(Object.assign({}, issue), { markedForSuggestion: false, status: IssueStatusEnum.Detected, updatedAt: new Date().toISOString() }) : issue) });
            };
            // Optimistically update ALL cache entries (list + grid)
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesAll(inspectionId) }, applyUpdate);
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) }, applyUpdate);
            return { previousQueries };
        }),
        // Roll back ALL cache entries on error
        onError: (err, variables, context) => {
            if (context === null || context === void 0 ? void 0 : context.previousQueries) {
                context.previousQueries.forEach(({ queryKey, data }) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            console.error('Error bulk undoing issues:', err);
        },
        // Always refetch after error or success to ensure we have the correct data
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: iimReviewKeys.issuesAll(inspectionId) });
            void queryClient.invalidateQueries({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) });
        }
    });
}
export default useUndoStatusIssue;
